import styled from '@emotion/styled'
import { Fade, useMediaQuery } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import Icon from '@f/elements/Icon'

import { useImageGalleryContext } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryContext'
import { GALLERY_NAVBAR_HEIGHT } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryNavigation'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { ClientOnly } from '@f-fertighaus/components/ClientOnly/ClientOnly'

const NEXT_IMG_ANIMATION_TIME = 200

export const ImageGalleryFocusView = () => {
  const isMobileView = useMediaQuery('(max-width: 767px)')
  const {
    state: { focusView }
  } = useImageGalleryContext()

  useEffect(() => {
    if (focusView.open) {
      webtrack({
        category: 'image',
        action: 'display',
        location: 'image-zoom',
        values: [
          {
            type: 'url',
            value:
              focusView.imageSources?.[focusView.displayImgIndex]?.src || ''
          }
        ]
      })
    }
  }, [focusView])

  if (!focusView.imageSources?.length || !focusView.open) {
    return null
  }

  return isMobileView ? (
    <ImageGalleryFocusViewMobile />
  ) : (
    <ImageGalleryFocusViewDesktop />
  )
}

const ImageGalleryFocusViewMobile = () => {
  const {
    state: { focusView },
    dispatch
  } = useImageGalleryContext()

  const handleSwipe = ({ realIndex }: { realIndex: number }) => {
    if (realIndex !== focusView.displayImgIndex && focusView.imageSources) {
      dispatch({
        type: 'changeFocusViewImage',
        displayImgIndex: realIndex
      })
      const imagesLastIndex = focusView.imageSources.length - 1
      const lastToFirstSwipe =
        realIndex === 0 && focusView.displayImgIndex === imagesLastIndex
      const firstToLastSwipe =
        realIndex === imagesLastIndex && focusView.displayImgIndex === 0

      const direction =
        (realIndex > focusView.displayImgIndex && !firstToLastSwipe) ||
        lastToFirstSwipe
          ? 'next'
          : 'prev'
      window._wt({
        category: 'CTA-button',
        action: 'click',
        label: `${direction}-image`,
        location: 'image-gallery',
        values: []
      })
    }
  }

  const allowSliding =
    focusView.imageSources && focusView.imageSources.length > 1

  return (
    <ClientOnly>
      <Wrapper>
        <Swiper
          style={{ width: '100%' }}
          loop
          initialSlide={focusView.displayImgIndex}
          allowSlideNext={allowSliding}
          allowSlidePrev={allowSliding}
          onSlideChange={handleSwipe}>
          {focusView.imageSources?.map(item => (
            <SwiperSlide key={item.src}>
              <Image src={item.src} alt={item.alt} />
            </SwiperSlide>
          ))}
        </Swiper>
      </Wrapper>
    </ClientOnly>
  )
}

const ImageGalleryFocusViewDesktop = () => {
  const [showImg, setShowImg] = useState<boolean>(true)
  const {
    state: { focusView },
    dispatch
  } = useImageGalleryContext()

  const handleImgChange = (direction: 'next' | 'prev') => {
    setShowImg(false)
    setTimeout(() => {
      if (direction === 'next') {
        moveToNextImage()
      } else {
        moveToPrevImage()
      }
      window._wt({
        category: 'CTA-button',
        action: 'click',
        label: `${direction}-image`,
        location: 'image-gallery',
        values: [{ type: 'text', value: '(ICON)' }]
      })
      setShowImg(true)
    }, NEXT_IMG_ANIMATION_TIME)
  }

  const moveToNextImage = () => {
    const newIndex =
      focusView.imageSources &&
      focusView.displayImgIndex === focusView.imageSources.length - 1
        ? 0
        : focusView.displayImgIndex + 1
    dispatch({ type: 'changeFocusViewImage', displayImgIndex: newIndex })
  }

  const moveToPrevImage = () => {
    const newIndex =
      focusView.displayImgIndex === 0 && focusView.imageSources
        ? focusView.imageSources.length - 1
        : focusView.displayImgIndex - 1
    dispatch({ type: 'changeFocusViewImage', displayImgIndex: newIndex })
  }

  useEffect(() => {
    const changeImageOnArrowPress = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handleImgChange('prev')
      } else if (event.key === 'ArrowRight') {
        handleImgChange('next')
      }
    }
    document.addEventListener('keydown', changeImageOnArrowPress, false)

    return () => {
      document.removeEventListener('keydown', changeImageOnArrowPress)
    }
  }, [handleImgChange])

  if (!focusView.imageSources?.length || !focusView.open) {
    return null
  }

  return (
    <Wrapper>
      {focusView.imageSources.length > 1 && (
        <NavButtonPrev onClick={() => handleImgChange('prev')}>
          <Icon icon='left-arrow-16px' className='[ f-icon-020 f-c-st-whi ]' />
        </NavButtonPrev>
      )}

      <Fade in={showImg} timeout={NEXT_IMG_ANIMATION_TIME} appear={false}>
        <ImageContainer>
          <Image
            src={focusView.imageSources[focusView.displayImgIndex].src}
            alt={focusView.imageSources[focusView.displayImgIndex].alt}
          />
        </ImageContainer>
      </Fade>
      {focusView.imageSources.length > 1 && (
        <NavButtonNext onClick={() => handleImgChange('next')}>
          <Icon icon='right-arrow-16px' className='[ f-icon-020 f-c-st-whi ]' />
        </NavButtonNext>
      )}
    </Wrapper>
  )
}

const Image = styled.img`
  max-height: calc(100vh - ${GALLERY_NAVBAR_HEIGHT});
  width: 100%;
  object-fit: scale-down;
  object-position: center;
`

const Wrapper = styled.div`
  position: fixed;
  top: ${GALLERY_NAVBAR_HEIGHT};
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - ${GALLERY_NAVBAR_HEIGHT});
  width: 100%;
  background: ${({ theme }) => theme.palette.common.black};
`

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.palette.common.white};
  cursor: pointer;
  background: transparent;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.palette.stone[500]};
  }
`

const NavButtonNext = styled(NavButton)`
  right: 16px;
`

const NavButtonPrev = styled(NavButton)`
  left: 16px;
`

const HALF_GALLERY_NAVBAR_HEIGHT = '28px' as const

const ImageContainer = styled.div`
  position: relative;
  transform: translateY(-${HALF_GALLERY_NAVBAR_HEIGHT});
  width: calc(100% - 160px);
  display: flex;
  justify-content: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    transform: none;
  }
`
